import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { track } from "@/lib/analytics";
import EmberCanvas from "@/components/EmberCanvas";
import { BRAND, HERO } from "@/data/content";

const EASE = [0.16, 1, 0.3, 1];
const SLIDE_MS = 5200;

export default function Hero() {
  const ref = useRef(null);
  const [slide, setSlide] = useState(0);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  useEffect(() => {
    const id = setInterval(() => {
      setSlide((s) => (s + 1) % HERO.slides.length);
    }, SLIDE_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      id="experience"
      ref={ref}
      className="relative min-h-screen overflow-hidden"
      data-testid="hero-section"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <AnimatePresence mode="sync">
          <motion.img
            key={HERO.slides[slide]}
            src={HERO.slides[slide]}
            alt={HERO.alt}
            data-testid="hero-slideshow-image"
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1.12 }}
            exit={{ opacity: 0 }}
            transition={{
              opacity: { duration: 1.6, ease: "easeInOut" },
              scale: { duration: SLIDE_MS / 1000 + 1.6, ease: "linear" },
            }}
            className="absolute inset-0 h-full w-full object-cover brightness-[1.12] contrast-[1.08] saturate-[1.45]"
          />
        </AnimatePresence>
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0908]/55 via-[#0A0908]/15 to-[#0A0908]" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A0908]/45 to-transparent" />
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-tr from-ember/20 via-transparent to-gold/10 mix-blend-overlay"
      />
      <EmberCanvas />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 flex min-h-screen flex-col justify-end px-6 pb-20 pt-32 sm:px-10 lg:px-16"
      >
        <motion.img
          src={BRAND.logo}
          alt="Rock Raven Live Fire Cooking"
          data-testid="hero-logo"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.05, ease: EASE }}
          className="mb-8 w-64 drop-shadow-[0_4px_24px_rgba(0,0,0,0.7)] sm:w-96 lg:w-[32rem]"
        />
        <motion.p
          data-testid="hero-eyebrow"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mb-6 max-w-[16rem] font-mono text-xs uppercase leading-relaxed tracking-[0.35em] text-amber-500/90 sm:max-w-none"
        >
          Rock Raven — Live Fire Cooking · Fredericksburg, TX
        </motion.p>

        <h1
          data-testid="hero-headline"
          className="font-serif text-4xl font-light uppercase leading-[1.05] tracking-tight text-amber-50 sm:text-5xl lg:text-6xl"
        >
          {HERO.lines.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.1, delay: 0.25 + i * 0.15, ease: EASE }}
              >
                {i === HERO.lines.length - 1 ? (
                  <span className="italic text-ember">{line}</span>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subheadline"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
          className="mt-6 max-w-xl text-base font-light leading-relaxed text-neutral-300 sm:text-lg"
        >
          {HERO.subheadline}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#contact"
            data-testid="hero-contact-cta"
            onClick={() => track("cta_click", { which: "hero-contact" })}
            className="group inline-flex items-center gap-3 bg-ember px-7 py-4 font-mono text-xs uppercase tracking-[0.25em] text-[#0A0908] transition-colors duration-300 hover:bg-amber-400"
          >
            Inquire for Dates
            <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <a
            href="#gallery"
            data-testid="hero-gallery-cta"
            className="inline-flex items-center gap-3 border border-amber-500/30 px-7 py-4 font-mono text-xs uppercase tracking-[0.25em] text-amber-200 transition-colors duration-300 hover:border-amber-400 hover:text-amber-50"
          >
            Explore Gallery
          </a>
        </motion.div>

        <motion.div
          data-testid="hero-stat-chips"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.25 }}
          className="mt-14 flex flex-wrap gap-3"
        >
          {HERO.stats.map((s) => (
            <div
              key={s.label}
              className="border border-amber-500/15 bg-black/40 px-5 py-3 backdrop-blur-sm"
            >
              <span className="font-serif text-xl text-amber-400">{s.value}</span>
              <span className="ml-3 font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                {s.label}
              </span>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
